import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from './api/client';
import type { LocalesResponse } from './types';

const RTL_LOCALES = new Set(['ar', 'fa', 'he', 'ur']);

interface LocaleState {
  current: string;
  available: { code: string; label: string }[];
  default: string;
  setCurrent: (code: string) => void;
  hydrate: (resp: LocalesResponse) => void;
}

export const LOCALE_LABEL_FALLBACK: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  ar: 'العربية',
  yo: 'Yorùbá',
  ig: 'Igbo',
  ha: 'Hausa',
  ff: 'Fulfulde',
  sw: 'Kiswahili',
  zh: '中文',
};

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      current: 'en',
      available: Object.entries(LOCALE_LABEL_FALLBACK).map(([code, label]) => ({ code, label })),
      default: 'en',
      setCurrent: (code) => set({ current: code }),
      hydrate: (resp) => {
        // Defensive: if the response is malformed (e.g. API not reachable, the
        // proxy returned HTML, or a stale cached value), keep the existing roster
        // rather than blowing up the whole app.
        if (!resp || !Array.isArray(resp.items) || resp.items.length === 0) return;
        set((s) => ({
          available: resp.items,
          default: resp.default,
          // Keep current if it's still available; otherwise fall back to default.
          current: resp.items.some((i) => i.code === s.current) ? s.current : resp.default,
        }));
      },
    }),
    { name: 'von-admin-locale', partialize: (s) => ({ current: s.current }) },
  ),
);

export function isRtlLocale(code: string): boolean {
  return RTL_LOCALES.has(code);
}

export function localeColorClass(code: string): string {
  return `bg-locale-${code}/10 text-locale-${code}`;
}

export async function fetchLocales(): Promise<LocalesResponse> {
  return api.get<LocalesResponse>('/locales', undefined, { anonymous: true });
}
